import React, { useEffect, useState } from 'react';
import { getRandevuFiltreleri, getRandevuListesi, randevuSmsGonder } from '../services/api';

const para = (v) => (Number(v) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Açıklama sütunu için karakter sınırı (kullanıcı talebi: 2026-10-07 — "sütunlar
// birbirini ezmeyecek şekilde belli sayıda karakterle sınırlandıralım").
const ACIKLAMA_SINIR = 40;
const kisalt = (m) => {
  const s = (m || '').replace(/\s+/g, ' ').trim();
  if (!s) return '—';
  return s.length <= ACIKLAMA_SINIR ? s : s.slice(0, ACIKLAMA_SINIR - 1) + '…';
};

const bugun = () => new Date().toISOString().slice(0, 10);

const trTarih = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
};

// Cep telefonundan tıklanabilir tel: bağlantısı üretir. Numaralar veritabanında
// "0532 111 22 33", "532-111-22-33" gibi karışık biçimlerde duruyor; sadece rakamları
// alıp +90 ile normalize ediyoruz. 10 haneye oturmayan kayıtlar link yapılmaz, düz
// metin gösterilir — yanlış numara aramaktansa tıklanamaz olsun.
const telLink = (ham) => {
  if (!ham) return null;
  let r = ham.replace(/\D/g, '');
  if (r.length === 12 && r.startsWith('90')) r = r.slice(2);
  else if (r.length === 11 && r.startsWith('0')) r = r.slice(1);
  return r.length === 10 ? `+90${r}` : null;
};

const etiket = {
  fontSize: 11,
  fontWeight: 700,
  color: 'var(--med-primary)',
  display: 'block',
  marginBottom: 5,
  textTransform: 'uppercase',
  letterSpacing: '.4px',
};

export default function RandevuListesi() {
  const [baslangic, setBaslangic] = useState(bugun());
  const [bitis, setBitis] = useState(bugun());
  const [hastaAdi, setHastaAdi] = useState('');
  const [dokKod, setDokKod] = useState('');
  const [hizmetKod, setHizmetKod] = useState('');
  const [geldi, setGeldi] = useState('');
  // ÇIKIŞ (ön fatura) süzgeci — ölçüt: iptal edilmemiş REFERANS kaydı (2026-09-21).
  const [cikis, setCikis] = useState('');

  const [uzmanlar, setUzmanlar] = useState([]);
  const [hizmetler, setHizmetler] = useState([]);
  const [liste, setListe] = useState([]);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const [bilgi, setBilgi] = useState('');
  const [arandi, setArandi] = useState(false);
  const [secili, setSecili] = useState(null);       // SMS için seçilen randevu
  const [smsAcik, setSmsAcik] = useState(false);
  const [smsMetni, setSmsMetni] = useState('');
  const [smsGonderiliyor, setSmsGonderiliyor] = useState(false);

  useEffect(() => {
    getRandevuFiltreleri()
      .then(res => {
        setUzmanlar(res.data.uzmanlar || []);
        setHizmetler(res.data.hizmetler || []);
      })
      .catch(() => setHata('Filtre listeleri yüklenemedi.'));
  }, []);

  const listele = async (e) => {
    if (e) e.preventDefault();
    setHata('');
    if (!baslangic || !bitis) { setHata('Başlangıç ve bitiş tarihi zorunludur.'); return; }
    if (bitis < baslangic) { setHata('Bitiş tarihi başlangıç tarihinden önce olamaz.'); return; }

    setYukleniyor(true);
    try {
      // Boş bırakılan filtreler isteğe hiç eklenmez — backend'de de uygulanmazlar.
      const params = { baslangic, bitis };
      if (hastaAdi.trim()) params.hastaAdi = hastaAdi.trim();
      if (dokKod) params.dokKod = dokKod;
      if (hizmetKod) params.hizmetKod = hizmetKod;
      if (geldi !== '') params.geldi = geldi;
      if (cikis !== '') params.cikis = cikis;

      const res = await getRandevuListesi(params);
      setListe(res.data);
      setSecili(null);
      setArandi(true);
    } catch (err) {
      setHata(err.response?.data?.hata || 'Randevu listesi alınamadı.');
      setListe([]);
    } finally {
      setYukleniyor(false);
    }
  };

  const temizle = () => {
    setBaslangic(bugun());
    setBitis(bugun());
    setHastaAdi('');
    setDokKod('');
    setHizmetKod('');
    setGeldi('');
    setCikis('');
    setListe([]);
    setSecili(null);
    setArandi(false);
    setHata('');
  };

  // Excel raporu — gerçek .xlsx (ExcelJS), dondurulmuş başlık + süzgeç.
  const excelAktar = async () => {
    if (liste.length === 0) { setHata('Listeniz boş!'); return; }
    setHata(''); setBilgi('Excel hazırlanıyor...');
    const ExcelJS = (await import('exceljs')).default;
    const kitap = new ExcelJS.Workbook();
    kitap.creator = 'FizyofiPlus';
    kitap.created = new Date();
    const sayfa = kitap.addWorksheet('Randevu Listesi', {
      views: [{ state: 'frozen', ySplit: 3 }],
      pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
    });

    const sutun = [
      { header: 'Tarih', key: 'tarih', width: 12 },
      { header: 'Saat', key: 'saat', width: 8 },
      { header: 'Hasta No', key: 'kayitNo', width: 11 },
      { header: 'Hasta Adı', key: 'hastaAdi', width: 32 },
      { header: 'Telefon', key: 'telefon', width: 18 },
      { header: 'Verilen Hizmet', key: 'hizmet', width: 30 },
      { header: 'Uzman', key: 'uzman', width: 26 },
      { header: 'Geliş', key: 'gelis', width: 11 },
      { header: 'Çıkış', key: 'cikisDurum', width: 14 },
      { header: 'Fatura Tutarı', key: 'faturaTutari', width: 15 },
      { header: 'Protokol', key: 'masterProtNo', width: 12 },
      // Excel'de KISALTILMAZ: ekranda yer sorunu var, dosyada yok (2026-10-07).
      { header: 'Açıklama', key: 'aciklama', width: 48 },
    ];
    sayfa.columns = sutun;
    sayfa.spliceRows(1, 0, [], []);
    sayfa.mergeCells(1, 1, 1, sutun.length);
    sayfa.mergeCells(2, 1, 2, sutun.length);
    const b1 = sayfa.getCell('A1');
    b1.value = 'FizyofiPlus · Randevu Listesi';
    b1.font = { size: 15, bold: true, color: { argb: 'FF1F2F46' } };
    sayfa.getRow(1).height = 24;
    const gelenSayisi = liste.filter(r => r.geldi).length;
    const cikanSayisi = liste.filter(r => r.cikisYapildi).length;
    const toplamTutar = liste.reduce((t, r) => t + (Number(r.faturaTutari) || 0), 0);
    sayfa.getCell('A2').value =
      `${trTarih(baslangic)} – ${trTarih(bitis)} · ${liste.length} randevu · ${gelenSayisi} geldi · `
      + `${cikanSayisi} çıkış yapıldı · toplam fatura ${para(toplamTutar)} TL`;
    sayfa.getCell('A2').font = { size: 10, color: { argb: 'FF6B7A8C' } };

    const basSatir = sayfa.getRow(3);
    sutun.forEach((s, i) => { basSatir.getCell(i + 1).value = s.header; });
    basSatir.height = 20;
    basSatir.eachCell(h => {
      h.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      h.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1F2F46' } };
      h.alignment = { vertical: 'middle' };
      h.border = { bottom: { style: 'thin', color: { argb: 'FF8C99A8' } } };
    });

    // Ekranda en yeni randevu üstte (azalan); Excel'de ise ESKİDEN YENİYE sıralanır
    // (kullanıcı talebi: 2026-09-24). Ekranın sırası bilerek değiştirilmedi.
    const sirali = [...liste].sort((a, b) => {
      const t = String(a.tarih ?? '').slice(0, 10).localeCompare(String(b.tarih ?? '').slice(0, 10));
      return t !== 0 ? t : String(a.saat ?? '').localeCompare(String(b.saat ?? ''));
    });

    sirali.forEach((r, i) => {
      const satir = sayfa.addRow({
        tarih: trTarih(r.tarih),
        saat: r.saat || '',
        kayitNo: r.kayitNo || '',
        hastaAdi: (r.hastaAdi || '').trim(),
        telefon: (r.telefon || '').trim(),
        hizmet: (r.hizmet || '').trim(),
        uzman: (r.uzman || '').trim(),
        gelis: r.geldi ? 'Geldi' : 'Gelmedi',
        cikisDurum: r.cikisYapildi ? 'Çıkış Yapıldı' : '—',
        faturaTutari: Number(r.faturaTutari) || 0,
        masterProtNo: r.masterProtNo || '',
        aciklama: (r.aciklama || '').replace(/\s+/g, ' ').trim(),
      });
      satir.getCell('E').numFmt = '@';          // telefon metin kalsın
      satir.getCell('J').numFmt = '#,##0.00';
      if (i % 2) satir.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F6FA' } }; });
      satir.eachCell(c => { c.border = { bottom: { style: 'hair', color: { argb: 'FFD6DCE4' } } }; });
    });

    sayfa.autoFilter = { from: { row: 3, column: 1 }, to: { row: 3, column: sutun.length } };

    const veri = await kitap.xlsx.writeBuffer();
    const url = URL.createObjectURL(new Blob([veri], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `randevu-listesi-${baslangic}_${bitis}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
    setBilgi(`${liste.length} randevu Excel'e aktarıldı.`);
  };

  const smsYolla = async () => {
    if (!secili) return;
    const metin = smsMetni.trim();
    if (!metin) { setHata('Lütfen mesaj metnini yazınız.'); return; }
    setSmsGonderiliyor(true); setHata('');
    try {
      const res = await randevuSmsGonder(secili.id, metin);
      setBilgi(res.data?.mesaj || 'SMS yollandı.');
      setSmsAcik(false);
      setSmsMetni('');
    } catch (err) {
      setHata(err.response?.data?.hata || 'SMS yollanamadı.');
    } finally {
      setSmsGonderiliyor(false);
    }
  };

  return (
    <div style={{ margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4, color: 'var(--med-dark)' }}>📋 Randevu Listesi</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: 20 }}>
        Tarih aralığı zorunludur; diğer filtreler boş bırakılırsa uygulanmaz.
      </p>

      <form onSubmit={listele} className="card" style={{ marginBottom: 16 }}>
        {/* Yedi filtre tek satırda; ekran genişliği sınırı kaldırıldığı için tam adlar sığıyor
            (kullanıcı talebi: 2026-09-21). Dar ekranda yine kendiliğinden alta iner. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
          <div>
            <label style={etiket}>Başlangıç Tarihi *</label>
            <input type="date" value={baslangic} onChange={e => setBaslangic(e.target.value)} required />
          </div>
          <div>
            <label style={etiket}>Bitiş Tarihi *</label>
            <input type="date" value={bitis} onChange={e => setBitis(e.target.value)} required />
          </div>
          <div>
            <label style={etiket}>Hasta Adı</label>
            <input type="text" value={hastaAdi} onChange={e => setHastaAdi(e.target.value)} placeholder="Tümü" />
          </div>
          <div>
            <label style={etiket}>Uzman</label>
            <select value={dokKod} onChange={e => setDokKod(e.target.value)}>
              <option value="">Tümü</option>
              {uzmanlar.map(u => <option key={u.kod} value={u.kod}>{u.adi}</option>)}
            </select>
          </div>
          <div>
            <label style={etiket}>Verilen Hizmet</label>
            <select value={hizmetKod} onChange={e => setHizmetKod(e.target.value)}>
              <option value="">Tümü</option>
              {hizmetler.map(h => <option key={h.kod} value={h.kod}>{h.adi}</option>)}
            </select>
          </div>
          <div>
            <label style={etiket}>Geldi mi?</label>
            <select value={geldi} onChange={e => setGeldi(e.target.value)}>
              <option value="">Tümü</option>
              <option value="1">Geldi</option>
              <option value="0">Gelmedi</option>
            </select>
          </div>
          <div>
            <label style={etiket}>Çıkış (Fatura)</label>
            <select value={cikis} onChange={e => setCikis(e.target.value)}>
              <option value="">Tümü</option>
              <option value="1">Çıkışı yapıldı</option>
              <option value="0">Çıkışı yapılmadı</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button type="submit" className="btn btn-primary" disabled={yukleniyor}>
            {yukleniyor ? 'Listeleniyor...' : '🔎 Listele'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={temizle} disabled={yukleniyor}>
            Temizle
          </button>
          <button type="button" className="btn btn-secondary" onClick={excelAktar}
            disabled={yukleniyor || liste.length === 0} title="Listeyi Excel'e aktar">
            📊 Excel'e Aktar
          </button>
          <button type="button" className="btn btn-secondary"
            onClick={() => { setSmsAcik(true); setHata(''); }}
            disabled={yukleniyor || !secili}
            title={secili ? `${secili.hastaAdi} kişisine SMS yolla` : 'Önce listeden bir randevu seçiniz'}>
            ✉️ SMS Yolla{secili ? ` (${secili.hastaAdi})` : ''}
          </button>
        </div>
      </form>

      {hata && <div className="hata-kutu">{hata}</div>}
      {bilgi && !hata && (
        <div style={{
          background: '#E6F4EA', border: '1px solid #A8D5B5', color: '#1E6B3A',
          borderRadius: 6, padding: '7px 12px', fontSize: 13, fontWeight: 600, marginBottom: 10,
        }}>{bilgi}</div>
      )}

      {arandi && !yukleniyor && (
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 8 }}>
          {liste.length} kayıt bulundu.
        </div>
      )}

      {yukleniyor && <div className="loading">Yükleniyor...</div>}

      {!yukleniyor && arandi && liste.length === 0 && (
        <div className="empty">Bu filtrelerle randevu bulunamadı.</div>
      )}

      {!yukleniyor && liste.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table className="veri-tablosu">
            <thead>
              <tr>
                <th>Tarih</th>
                <th>Saat</th>
                <th>Hasta Adı</th>
                <th>Telefon</th>
                <th>Verilecek Hizmet</th>
                <th>Geliş</th>
                <th>Çıkış</th>
                <th>Uzman</th>
                <th>Açıklama</th>
              </tr>
            </thead>
            <tbody>
              {liste.map(r => {
                const tel = telLink(r.telefon);
                return (
                  <tr key={r.id}
                    onClick={() => setSecili(r)}
                    style={{
                      cursor: 'pointer',
                      background: secili?.id === r.id ? '#E4EEFB' : undefined,
                      boxShadow: secili?.id === r.id ? 'inset 3px 0 0 var(--med-primary)' : undefined,
                    }}>
                    <td style={{ whiteSpace: 'nowrap' }}>{trTarih(r.tarih)}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>{r.saat}</td>
                    <td>{r.hastaAdi}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {tel
                        ? <a href={`tel:${tel}`} style={{ color: 'var(--med-primary)', fontWeight: 500 }}>{r.telefon}</a>
                        : <span style={{ color: 'var(--text-muted)' }}>{r.telefon || '—'}</span>}
                    </td>
                    <td>{r.hizmet}</td>
                    <td>
                      <span className={`badge ${r.geldi ? 'badge-shift10' : 'badge-parttime'}`}>
                        {r.geldi ? 'Geldi' : 'Gelmedi'}
                      </span>
                    </td>
                    {/* ÇIKIŞ = iptal edilmemiş fatura (REFERANS) kaydı var mı. */}
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {r.cikisYapildi
                        ? <span style={{
                            background: '#D6EEDD', border: '1px solid #8FC7A2', color: '#14532D',
                            borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700,
                          }}>
                            Çıkış Yapıldı{Number(r.faturaTutari) > 0 ? ` · ${para(r.faturaTutari)} TL` : ''}
                          </span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td>{r.uzman}</td>
                    {/* AÇIKLAMA (RAND.ACK) — kullanıcı talebi: 2026-10-07.
                        "Tooltip yetmiyor arkadaşlara, listeye yazsın." Tamamı yazılmaz:
                        ölçüldü, notların en uzunu 237 karakter; tabloyu ezerdi. İlk
                        ACIKLAMA_SINIR karakter gösterilir, tamamı üstüne gelince çıkar. */}
                    <td style={{ maxWidth: 230, color: r.aciklama ? undefined : 'var(--text-muted)' }}
                        title={r.aciklama || ''}>
                      {kisalt(r.aciklama)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* SMS penceresi — seçili randevunun hastasına tek mesaj (2026-09-21). */}
      {smsAcik && secili && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(15,30,55,.45)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '10vh 16px',
        }}>
          <div className="card" style={{ width: '100%', maxWidth: 460, padding: 0, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '9px 12px 9px 14px', color: '#fff',
              background: 'linear-gradient(90deg,#1F3F7A,#3E63A8 70%,#6E8DC8)',
            }}>
              <strong style={{ fontSize: 14 }}>SMS Yolla</strong>
              <button type="button" onClick={() => setSmsAcik(false)}
                style={{ width: 26, height: 24, border: 'none', borderRadius: 4, background: '#D9534F', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>✕</button>
            </div>
            <div style={{ padding: '12px 14px 14px' }}>
              <p style={{ margin: '0 0 8px', fontSize: 13, lineHeight: 1.5 }}>
                <b>{secili.hastaAdi}</b><br />
                <span style={{ color: 'var(--text-muted)' }}>
                  {secili.telefon || 'Randevuda telefon yok — hasta kartındaki cep denenecek'} ·
                  {' '}{trTarih(secili.tarih)} {secili.saat}
                </span>
              </p>
              <textarea rows={4} value={smsMetni} maxLength={300}
                onChange={e => setSmsMetni(e.target.value)}
                placeholder="Mesaj metni..."
                style={{ width: '100%', fontSize: 13, resize: 'vertical' }} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'right', marginTop: 2 }}>
                {smsMetni.length} / 300
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 10 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setSmsAcik(false)}>Vazgeç</button>
                <button type="button" className="btn btn-primary" onClick={smsYolla}
                  disabled={smsGonderiliyor || !smsMetni.trim()}>
                  {smsGonderiliyor ? 'Yollanıyor...' : 'SMS Yolla'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
